import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import './Toast.css';

/* ── Contexte ─────────────────────────────────────────────────── */
const ToastContext = createContext(null);

const ICONS = {
  success: 'fas fa-circle-check',
  error:   'fas fa-circle-xmark',
  warning: 'fas fa-triangle-exclamation',
  info:    'fas fa-circle-info',
};

/* ── Provider ─────────────────────────────────────────────────── */
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const counter = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const toast = useCallback((message, { type = 'info', duration = 4000, title } = {}) => {
    const id = ++counter.current;
    setToasts(prev => [...prev, { id, type, message, title }]);
    if (duration > 0) {
      setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  /* Raccourcis */
  toast.success = (msg, opts) => toast(msg, { type: 'success', ...opts });
  toast.error   = (msg, opts) => toast(msg, { type: 'error',   duration: 6000, ...opts });
  toast.warning = (msg, opts) => toast(msg, { type: 'warning', ...opts });
  toast.info    = (msg, opts) => toast(msg, { type: 'info',    ...opts });

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="toast-portal" aria-live="polite" aria-atomic="false">
        <AnimatePresence initial={false}>
          {toasts.map(t => (
            <motion.div
              key={t.id}
              className={`toast toast--${t.type}`}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0,  scale: 1    }}
              exit={{    opacity: 0, y: -8, scale: 0.95, transition: { duration: 0.18 } }}
              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              layout
            >
              <span className="toast__icon" aria-hidden="true">
                <i className={ICONS[t.type]} />
              </span>

              <div className="toast__body">
                {t.title && <strong className="toast__title">{t.title}</strong>}
                <span className="toast__message">{t.message}</span>
              </div>

              <button
                className="toast__close"
                onClick={() => dismiss(t.id)}
                aria-label="Fermer"
              >
                <i className="fas fa-xmark" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};

/* ── Hook ─────────────────────────────────────────────────────── */
export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast doit être utilisé dans <ToastProvider>');
  return ctx;
};

export default ToastProvider;
